import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Moment } from "moment";
import {
  dateSelected,
  setSelectedGroup,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";

import { RootState } from "../../../redux/store";
import { Assets } from "../../../assets/assets";
import BookingDatePicker from "../common/components/OrderDataStep/BookingDatePicker";
import ListLoadError from "../common/components/ListLoadError";
import { useBookingWindow } from "../common/hooks/useBookingWindow";
import { pickedDayLabel, stationDayLabel } from "../../../util/stationTime";
import { parcelAvailableDates } from "../util/orderDataStepHelper";
import { useSelectedLocationTimeZone } from "../../../util/locationTimeZone";

const styles = {
  root: {
    padding: "2px",
    paddingTop: 0,
  },
  dateTimeWrapper: {
    display: "flex",
    flexDirection: "column",
    
  },
  inputWrapper: {
    width: "350px",
    display: "flex",
    flexDirection: "column",
    margin: `12px auto 0 auto`,
    "@media (min-width: 1280px)": {
      width: "350px",
    },
  },
};
const PackageDetailsStep = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  // The tenant's configured booking horizon for this use case — the picker
  // used to cap dates at a hardcoded 18 months, which contradicted the one
  // month the availability request already asked for. One month is the
  // default here, so the effective behaviour is unchanged.
  const bookingWindow = useBookingWindow("parcel");
  const InfoIconWhiteFilled = Assets.icon.infoIconWhiteFilled.component;
  const { selectedLocation, selectedGroup } = useSelector(
    (state: RootState) => state.bookingCreation
  );

  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );
  
  // Generate available dates without past dates in "DD.MM.YYYY" format
  // The station's today (#3219): the location's clock, not the phone's.
  const timeZone = useSelectedLocationTimeZone();
  const availableDates = useMemo(() => parcelAvailableDates(Date.now(), timeZone), [timeZone]);

  const groupsByLocation = useSelector(
    (state: RootState) => state.bookingCreation.groupsByLocation
  );

  const storages = useMemo(() => {
    return selectedLocation ? groupsByLocation[selectedLocation.id] || [] : [];
  }, [selectedLocation, groupsByLocation]);

  // Without this a failed load leaves an empty size dropdown, which reads as
  // "this station offers nothing" rather than "we could not load it" (#1970).
  const loadFailed = useSelector(
    (state: RootState) =>
      !!selectedLocation &&
      state.bookingCreation.groupsLoadFailedLocationId === selectedLocation.id
  );

  const handlePackageSizeChange = (itemId: string) => {
    const storage = storages.find((storage) => storage.id === itemId);
    if (storage) {
      dispatch(setSelectedGroup(storage));
    }
  };
  const handleDateChange = (date: Moment | null) => {
    dispatch(dateSelected({ date }));
  };
  
  // Function to disable past dates - handle null case. Past = before the
  // station's today, compared as calendar-day labels.
  const shouldDisableDate = (date: Moment | null) => {
    if (!date) return true; // Disable if date is null
    return pickedDayLabel(date) < stationDayLabel(Date.now(), timeZone);
  };

  if (loadFailed) {
    return <ListLoadError messageKey="book_location.error.fetch_equipment" />;
  }

  return (
    <Box sx={{ padding: "20px" }}>
      <Box sx={{ maxWidth: "400px", margin: "0 auto" }}>
        <Box sx={{ marginBottom: "20px" }}>
          <Select
            value={selectedGroup?.id}
            onValueChange={handlePackageSizeChange}
          >
            <SelectTrigger
              aria-label={t(
                "book_location.package_details.size_label",
                "Select package size"
              )}
            >
              <SelectValue
                placeholder={t(
                  "book_location.package_details.size_label",
                  "Select package size"
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {storages.map((storage) => (
                <SelectItem key={storage.id} value={storage.id}>
                  {storage.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Box>
        {selectedGroup && (
          <Box sx={{ display: "flex", paddingBottom: 3 }}>
            <Box sx={{ marginRight: "5px", color: "#1E1E1E" }}>
              <InfoIconWhiteFilled
                style={{
                  width: "20px",
                  height: "20px",
                  // color: '#1E1E1E'
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight="bold">
              {selectedGroup?.langNames?.[i18n.language]?.description}
            </Typography>
          </Box>
        )}

        <Box sx={styles.dateTimeWrapper}>
          <BookingDatePicker
            value={orderData.date}
            onChange={(date) => handleDateChange(date)}
            shouldDisableDate={shouldDisableDate}
            availableDates={availableDates}
            maxDate={bookingWindow.latest}
            label={t("book_location.package_details.date_label")}
          />
        </Box>

        <Box
          sx={{
            backgroundColor: "#F5F5F5",
            padding: "12px",
            borderRadius: "4px",
            marginBottom: "20px",
          }}
        >
          <Typography variant="body2" sx={{ color: "#666" }}>
            <strong>
              {t("book_location.package_details.note_label", "NB")}:{" "}
            </strong>
            {t(
              "book_location.package_details.note",
              "No need to worry if you don't already have the above information. You can skip as the final selection will be done by the delivery agent."
            )}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PackageDetailsStep;